import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { BrandS } from '@sorare/core/src/atoms/typography';
import { ConfirmSlider } from '@sorare/core/src/atoms/ui/ConfirmSlider';
import { useResultsStoryAssets } from '@sorare/core/src/components/animations/AnimatedStory/useResultsStoryAssets';
import { ShouldVerifyUserBeforeClaiming } from '@sorare/core/src/components/rewards/ShouldVerifyUserBeforeClaiming';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { isType, isTypeFn } from '@sorare/core/src/lib/gql';

import { Title } from '#src/components/so5/LastFixtureStory/LastFixtureStoryStep/Title/index.tsx';
import { LastFixtureStoryStep } from '#src/components/so5/LastFixtureStory/LastFixtureStoryStep/index.tsx';
import { useRewardsVerification } from '#src/components/so5/LastFixtureStory/RewardsRecap/useRewardsVerification.ts';
import { useClaimRewards } from '#src/hooks/so5/useClaimRewards.ts';

import { ClaimRewardConfirmDialog } from './RewardWithAlternaltiveReward/ClaimRewardConfirmDialog/index.tsx';
import { RewardWithAlternaltiveReward } from './RewardWithAlternaltiveReward/index.tsx';
import type {
  CardRewardsReveal_so5Fixture,
  CardRewardsReveal_so5Reward,
} from './__generated__/index.graphql.ts';

const Wrapper = styled.div`
  width: 100%;
  & > * {
    width: 100%;
  }
`;

const hasBlockchainReward = (so5Reward: CardRewardsReveal_so5Reward) => {
  return (
    // Has at least one rarity card
    !so5Reward.rewards
      .filter(isTypeFn('AnyCardReward'))
      .every(({ card }) => card.rarity === Rarity.common) ||
    // Has at least one cash reward
    so5Reward.rewards.some(r => isType(r, 'MonetaryReward'))
  );
};

type ContentProps = {
  so5Rewards: CardRewardsReveal_so5Reward[];
  selectedAlternativeRewards: Record<string, string | null>;
  onAlternativeRewardClick: (
    so5RewardId: string,
    signedAlternativeRewardConfigs: string
  ) => void;
  onReveal: (so5RewardId: string) => void;
};
const Content = ({
  so5Rewards,
  selectedAlternativeRewards,
  onAlternativeRewardClick,
  onReveal,
}: ContentProps) => {
  const isTabletAndAbove = useIsTabletAndAbove();

  const itemToDisplayOnDesktop = Math.min(3, so5Rewards.length);
  const itemToDisplay = isTabletAndAbove ? itemToDisplayOnDesktop : 1;
  const wrapperWidth = `calc(${itemToDisplay} * ${isTabletAndAbove ? '250px' : 'calc(60 * var(--vw))'} + var(--double-unit) * ${itemToDisplay - 1})`;

  return (
    <div
      style={{
        width: wrapperWidth,
      }}
    >
      <Scrollable itemToDisplay={itemToDisplay} withMask>
        {so5Rewards.map(so5Reward => {
          return (
            <Wrapper key={so5Reward.slug}>
              <ShouldVerifyUserBeforeClaiming
                hasBlockchainRewards={hasBlockchainReward(so5Reward)}
              >
                {({ disabledClaim }) => {
                  return (
                    <RewardWithAlternaltiveReward
                      so5Reward={so5Reward}
                      selectedSignedAlternativeRewardConfigs={
                        selectedAlternativeRewards[so5Reward.id]
                      }
                      onAlternativeRewardClick={signedAlternativeRewardConfigs =>
                        onAlternativeRewardClick(
                          so5Reward.id,
                          signedAlternativeRewardConfigs
                        )
                      }
                      onReveal={onReveal}
                      disabledClaim={disabledClaim}
                    />
                  );
                }}
              </ShouldVerifyUserBeforeClaiming>
            </Wrapper>
          );
        })}
      </Scrollable>
    </div>
  );
};

type Props = {
  so5Fixture?: CardRewardsReveal_so5Fixture;
  so5Rewards: CardRewardsReveal_so5Reward[];
  onNext: ComponentProps<typeof LastFixtureStoryStep>['onNext'];
  onClose: ComponentProps<typeof LastFixtureStoryStep>['onClose'];
  setSo5RewardsWithSignedAlternative?: (
    arg: {
      so5RewardId: string;
      signedAlternativeRewardConfigs: string | null;
    }[]
  ) => void;
};
export const CardRewardsReveal = ({
  so5Fixture,
  so5Rewards,
  onNext,
  onClose,
  setSo5RewardsWithSignedAlternative,
}: Props) => {
  const [claimRewards] = useClaimRewards();
  const [selectedAlternativeRewards, setSelectedAlternativeRewards] = useState(
    so5Rewards.reduce<Record<string, string | null>>((obj, { id }) => {
      obj[id] = null;
      return obj;
    }, {})
  );
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const { tunnel } = useResultsStoryAssets();
  const { rewardsByType, verificationNeeded } =
    useRewardsVerification(so5Rewards);
  const revealableRewards = useRef([
    ...rewardsByType.card,
    ...rewardsByType.cash,
    ...rewardsByType.shards,
  ]);

  const setAlternativeRewardForSpecificReward = (
    so5RewardSlug: string,
    signedAlternativeRewardConfigs: string
  ) => {
    setSelectedAlternativeRewards(current => {
      return {
        ...current,
        [so5RewardSlug]: !current[so5RewardSlug]
          ? signedAlternativeRewardConfigs
          : null,
      };
    });
  };

  const claimAllRewards = async () => {
    const so5RewardsWithSignedAlternative = Object.entries(
      selectedAlternativeRewards
    )
      .map(([so5RewardId, signedAlternativeRewardConfigs]) => ({
        so5RewardId,
        signedAlternativeRewardConfigs,
      }))
      .filter(Boolean);

    await claimRewards({
      so5RewardsWithSignedAlternative,
    });

    setSo5RewardsWithSignedAlternative?.(so5RewardsWithSignedAlternative);
  };

  const onConfirm = async () => {
    await claimAllRewards();
    onNext?.();
  };

  return (
    <LastFixtureStoryStep
      title={<Title type="cardReveal" so5Fixture={so5Fixture} />}
      video={{ asset: tunnel }}
      onClose={onClose}
      cta={
        <ConfirmSlider
          disabled={verificationNeeded.kyc || verificationNeeded.phoneNumber}
          disabledText={
            <FormattedMessage
              id="CardRewardsReveal.slider.disabled"
              defaultMessage="Need account verification"
            />
          }
          onConfirm={async () => {
            const allRevealableRewardsRevealed =
              revealableRewards.current.every(({ id }) => !!revealed[id]);
            if (allRevealableRewardsRevealed) {
              await onConfirm();
            } else {
              setConfirmDialogOpen(true);
            }
          }}
          ctaMessage={
            <BrandS>
              <FormattedMessage
                id="CardRewardsReveal.slider.text"
                defaultMessage="Swipe to confirm all"
              />
            </BrandS>
          }
        />
      }
    >
      <ClaimRewardConfirmDialog
        open={confirmDialogOpen}
        onConfirm={onConfirm}
        onClose={() => {
          setConfirmDialogOpen(false);
        }}
      />
      <Content
        so5Rewards={revealableRewards.current}
        selectedAlternativeRewards={selectedAlternativeRewards}
        onAlternativeRewardClick={(
          so5RewardId,
          signedAlternativeRewardConfigs
        ) =>
          setAlternativeRewardForSpecificReward(
            so5RewardId,
            signedAlternativeRewardConfigs
          )
        }
        onReveal={so5RewardId =>
          setRevealed(prev => ({
            ...prev,
            [so5RewardId]: true,
          }))
        }
      />
    </LastFixtureStoryStep>
  );
};

CardRewardsReveal.fragments = {
  so5Reward: gql`
    fragment CardRewardsReveal_so5Reward on So5Reward {
      slug
      rewards {
        id
        ... on AnyCardReward {
          id
          card {
            slug
            rarity: rarityTyped
          }
        }
      }
      ...useRewardsVerification_so5Reward
      ...RewardWithAlternaltiveReward_so5Reward
    }
    ${useRewardsVerification.fragments.so5Reward}
    ${RewardWithAlternaltiveReward.fragments.so5Reward}
  ` as TypedDocumentNode<CardRewardsReveal_so5Reward>,
  so5Fixture: gql`
    fragment CardRewardsReveal_so5Fixture on So5Fixture {
      slug
      type
      mySo5Rewards {
        slug
      }
      ...Title_so5Fixture
    }
    ${Title.fragments.so5Fixture}
  ` as TypedDocumentNode<CardRewardsReveal_so5Fixture>,
};
